import { useState } from 'react';
import { Link } from 'react-router-dom';
import MarketingLayout from '../../components/MarketingLayout';
import { tokens } from '../../components/SEOPageLayout';
import { BreadcrumbJsonLd, FaqJsonLd } from '../../components/StructuredData';
import { SeoLongformSections } from '../../components/SeoLongformSections';
import RequestAccessModal from '../../components/billing/RequestAccessModal';

export default function BranchProtectionDriftDetectionPage() {
  const [showRequestAccess, setShowRequestAccess] = useState(false);

  const faqs = [
    {
      question: 'What is branch protection drift?',
      answer: 'Branch protection drift occurs when branch protection rules or rulesets are modified, potentially weakening controls that enforce code review, testing, or approval requirements before merging changes.'
    },
    {
      question: 'How often does branch protection drift detection run?',
      answer: 'Drift detection runs on a scheduled basis (typically hourly) for enabled repositories. Check frequency varies by plan tier.'
    },
    {
      question: 'What happens when branch protection drift is detected?',
      answer: 'When drift is detected, EvidentTrail creates a drift change record showing what changed, who made the change, and when. This provides an audit trail for protection rule modifications.'
    },
    {
      question: 'Can I acknowledge expected drift changes?',
      answer: 'Yes. Drift changes can be acknowledged when they represent intentional, authorized modifications (e.g., adding stricter review requirements, updating required status checks).'
    },
    {
      question: 'What is the difference between branch protection monitoring and drift detection?',
      answer: 'Monitoring captures the current state of branch protection rules. Drift detection tracks changes over time, showing when rules were weakened, removed, or modified.'
    }
  ];

  const longformSections = [
    {
      title: 'What auditors ask for',
      paragraphs: [
        'Auditors need evidence that branch protection controls operated consistently throughout the audit period. Branch protection drift detection provides a change log showing when protection rules were modified, weakened, or removed.'
      ],
      bullets: [
        'Timeline of changes to branch protection rules',
        'Evidence that required reviewers were not reduced or removed',
        'Who authorized changes to protection settings',
        'Duration of any weakened protection periods',
        'Acknowledgement records for intentional rule modifications'
      ]
    },
    {
      title: 'Evidence artifacts included',
      paragraphs: [
        'Drift detection captures snapshots of branch protection configurations and rulesets, comparing them over time to identify modifications.'
      ],
      bullets: [
        'Required reviewer count changes (e.g., 2 reviewers reduced to 1)',
        'Required status check modifications (checks added or removed)',
        'Direct push restriction changes',
        'Admin bypass enforcement changes',
        'Ruleset modifications (GitHub Rulesets v2)',
        'Timestamps and author identity for each change'
      ]
    },
    {
      title: 'How EvidentTrail works (drift detection)',
      paragraphs: [
        'EvidentTrail periodically scans branch protection settings and rulesets, comparing current state to previous snapshots. Changes are flagged as drift and recorded for audit review.'
      ],
      bullets: [
        'Scheduled scans (typically hourly for enabled repos)',
        'Snapshot comparison to identify changes',
        'Drift change records with before/after states',
        'Acknowledgement workflow for expected changes',
        'Usage metering and quotas vary by plan tier'
      ]
    },
    {
      title: 'How to interpret the output',
      paragraphs: [
        'Review drift change records to identify when branch protection controls were modified. Acknowledged changes represent intentional modifications; unacknowledged changes may indicate unauthorized weakening of controls.'
      ],
      bullets: [
        'Check drift timeline for changes during audit window',
        'Verify modifications were authorized and acknowledged',
        'Review before/after states to assess control impact',
        'Cross-reference with exception records if controls were bypassed',
        'Export drift history for auditor review'
      ]
    },
    {
      title: 'Common failure modes',
      paragraphs: [
        'Drift detection can fail if repositories are not enabled for monitoring, if drift check quotas are exceeded, or if protection rules are deleted entirely rather than modified.'
      ],
      bullets: [
        'Repository not enabled for drift detection',
        'Monthly drift check quota exceeded',
        'Branch protection rules deleted (no before state to compare)',
        'Migration from legacy rules to rulesets (appears as removed + added)',
        'Drift changes not acknowledged, creating false audit findings'
      ]
    }
  ];

  return (
    <>
      <MarketingLayout
        title="Branch Protection Drift Detection for Compliance | EvidentTrail"
        description="Monitor changes to GitHub branch protection rules and rulesets. Detect when required reviewers, status checks, or enforcement settings are weakened during audit periods."
        h1="Branch Protection Drift Detection"
        heroText="Detect when branch protection rules are modified or weakened, creating an audit trail for control changes that affect code review and merge requirements."
        type="article"
      >
        <BreadcrumbJsonLd
          items={[
            { name: 'Home', url: 'https://evidenttrail.com/' },
            { name: 'Branch Protection Drift Detection', url: 'https://evidenttrail.com/branch-protection-drift-detection' }
          ]}
        />
        <FaqJsonLd items={faqs} />

        {/* Drift Detection Explained */}
        <section className={tokens.sectionAlt}>
          <div className={tokens.container}>
            <div className="max-w-3xl mx-auto">
              <h2 className={`${tokens.heading.h2} mb-6`}>What Branch Protection Drift Detection Monitors</h2>
              <p className={`${tokens.text.base} mb-6`}>
                Branch protection drift detection tracks changes to GitHub branch protection rules and rulesets, providing evidence that merge controls operated consistently during audit periods.
              </p>
              <p className={`${tokens.text.base} mb-6`}>
                See a full example in our{' '}
                <Link to="/sample-drift-event" className="text-primary-400 hover:underline">sample drift event</Link>
                output.
              </p>

              <div className={tokens.card}>
                <h3 className={`${tokens.heading.h3} mb-4`}>Monitored Elements</h3>
                <ul className="space-y-3">
                  <li className="flex items-start gap-3">
                    <span className="w-2 h-2 bg-primary-400 rounded-full mt-2 flex-shrink-0" />
                    <span className={tokens.text.base}>
                      <strong className="text-white">Required Reviewers:</strong> Changes to minimum approval count (e.g., 2 reviewers reduced to 1 or 0)
                    </span>
                  </li>
                  <li className="flex items-start gap-3">
                    <span className="w-2 h-2 bg-primary-400 rounded-full mt-2 flex-shrink-0" />
                    <span className={tokens.text.base}>
                      <strong className="text-white">Required Status Checks:</strong> CI checks required before merge (e.g., tests, lint, security scans)
                    </span>
                  </li>
                  <li className="flex items-start gap-3">
                    <span className="w-2 h-2 bg-primary-400 rounded-full mt-2 flex-shrink-0" />
                    <span className={tokens.text.base}>
                      <strong className="text-white">Direct Push Restrictions:</strong> Whether direct commits to protected branches are blocked
                    </span>
                  </li>
                  <li className="flex items-start gap-3">
                    <span className="w-2 h-2 bg-primary-400 rounded-full mt-2 flex-shrink-0" />
                    <span className={tokens.text.base}>
                      <strong className="text-white">Admin Enforcement:</strong> Whether admins can bypass protection rules
                    </span>
                  </li>
                  <li className="flex items-start gap-3">
                    <span className="w-2 h-2 bg-primary-400 rounded-full mt-2 flex-shrink-0" />
                    <span className={tokens.text.base}>
                      <strong className="text-white">Rulesets (v2):</strong> Modern GitHub Rulesets configuration changes
                    </span>
                  </li>
                </ul>
              </div>

              <h3 className={`${tokens.heading.h3} mb-4 mt-8`}>Correct Branch Protection Configuration</h3>
              <div className={`${tokens.card} mb-4`}>
                <div className="font-mono text-sm space-y-1 text-slate-300">
                  <div><strong className="text-white">Branch:</strong> main (production)</div>
                  <div><strong className="text-white">Required Approvals:</strong> 1</div>
                  <div><strong className="text-white">Required Status Checks:</strong> test-suite, lint, security-scan</div>
                  <div><strong className="text-white">Allow Force Push:</strong> false</div>
                  <div><strong className="text-white">Allow Deletions:</strong> false</div>
                  <div><strong className="text-white">Enforce for Admins:</strong> true</div>
                  <div><strong className="text-white">Require Linear History:</strong> true</div>
                </div>
              </div>
              <p className={`${tokens.text.small} text-slate-400 italic mb-8`}>
                This is the baseline configuration. Drift detection fires when any of these settings are weakened — for example, if required approvals drop to 0 or security-scan is removed from required checks.
              </p>

              <h3 className={`${tokens.heading.h3} mb-4 mt-8`}>Example: Branch Protection Drift Scenario</h3>
              <div className="grid md:grid-cols-2 gap-6 mb-4">
                <div className="border border-green-500/30 bg-green-950/20 rounded-lg p-6">
                  <h4 className="text-green-300 font-semibold mb-3">Before (Jan 15)</h4>
                  <div className="font-mono text-xs text-slate-300 space-y-1">
                    <div><strong>Required Reviewers:</strong> 2</div>
                    <div><strong>Required Checks:</strong></div>
                    <div>• test-suite</div>
                    <div>• security-scan</div>
                    <div className="mt-2"><strong>Direct Push:</strong> blocked</div>
                    <div><strong>Admin Bypass:</strong> disabled</div>
                  </div>
                </div>
                <div className="border border-amber-500/30 bg-amber-950/20 rounded-lg p-6">
                  <h4 className="text-amber-300 font-semibold mb-3">After (Jan 20)</h4>
                  <div className="font-mono text-xs text-slate-300 space-y-1">
                    <div><strong>Required Reviewers:</strong> 1</div>
                    <div><strong>Required Checks:</strong></div>
                    <div>• test-suite</div>
                    <div className="mt-2"><strong>Direct Push:</strong> blocked</div>
                    <div><strong>Admin Bypass:</strong> disabled</div>
                    <div className="mt-2 text-amber-300">⚠️ Reviewers: 2 → 1</div>
                    <div className="text-amber-300">⚠️ security-scan removed</div>
                  </div>
                </div>
              </div>
              <p className={`${tokens.text.small} text-slate-400 italic`}>
                Drift detected: Required reviewers reduced from 2 to 1, and security-scan removed from required checks. Auditor would review PRs merged between Jan 15-20 to determine if weakened controls affected code quality.
              </p>
            </div>
          </div>
        </section>

        <SeoLongformSections sections={longformSections} faqs={faqs} />

        {/* Internal Linking */}
        <section className={tokens.section}>
          <div className={tokens.container}>
            <h2 className={`${tokens.heading.h3} mb-8 text-center`}>Related Drift Detection Topics</h2>
            <div className="grid md:grid-cols-3 gap-6">
              <Link to="/soc2-github-compliance" className={`${tokens.card} hover:border-primary-500/50 transition-colors`}>
                <h4 className="text-white font-medium mb-2">SOC 2 GitHub Compliance</h4>
                <p className="text-sm text-slate-400">What SOC 2 auditors look for in branch protection settings.</p>
              </Link>
              <Link to="/github-workflow-drift-detection" className={`${tokens.card} hover:border-primary-500/50 transition-colors`}>
                <h4 className="text-white font-medium mb-2">Workflow Drift Detection</h4>
                <p className="text-sm text-slate-400">Track changes to GitHub Actions workflows.</p>
              </Link>
              <Link to="/exception-tracking" className={`${tokens.card} hover:border-primary-500/50 transition-colors`}>
                <h4 className="text-white font-medium mb-2">Exception Tracking</h4>
                <p className="text-sm text-slate-400">Document control bypasses with formal exceptions.</p>
              </Link>
            </div>
          </div>
        </section>

        {/* CTA */}
        <section className={tokens.sectionAlt}>
          <div className={tokens.container}>
            <div className="max-w-2xl mx-auto text-center">
              <h2 className={`${tokens.heading.h2} mb-4`}>Try Branch Protection Drift Detection</h2>
              <p className={`${tokens.text.base} mb-8`}>
                Request access to start monitoring branch protection rule changes for compliance.
              </p>
              <button 
                onClick={() => setShowRequestAccess(true)}
                className="btn btn-primary text-lg px-8 py-3"
              >
                Request Access
              </button>
            </div>
          </div>
        </section>
      </MarketingLayout>

      <RequestAccessModal 
        isOpen={showRequestAccess}
        onClose={() => setShowRequestAccess(false)}
        preselectedFeature="Branch Protection Drift Detection"
      />
    </>
  );
}
